import { useEffect, useState } from 'react'

import { Formik, FormikHelpers } from 'formik'
import { Form } from 'formik'
import styled from 'styled-components'

import {
  Box,
  Button,
  Flex,
  Modal,
} from 'app/components/design-system-components'
import { InputField } from 'app/components/design-system-components/inputs'
import * as getters from 'app/components/design-system-components/theme/getters'
import { Text } from 'app/components/design-system-components/typography'
import { FieldWrapper } from 'app/forms/FieldWrapper'
import { formValidationBuilder } from 'app/forms/utils/formValidationBuilder'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import type { Pod } from 'app/sm/team_viewing_preference/pods/helper'
import SelectTeamMembersDropdown from 'app/sm/team_viewing_preference/pods/SelectTeamMembersDropdown'

type AddPodModalProps = {
  teamID: string
  editingPod: Pod | undefined
  isEditmode: boolean
  onDismissClick: () => void
  onSubmitForm: ({
    name,
    managers,
  }: {
    name: string
    managers: string[]
  }) => void
}

const StyledFormBody = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.baseSpace}px 0;
`

const StyledFieldWrapper = styled(FieldWrapper)`
  :not(&:first-child) {
    margin-top: ${getters.spacing(5)};
  }
`

export const addPodsFormFields = {
  name: 'name',
  managers: 'managers',
}

const initialFormValues = {
  name: '',
  managers: {},
}

const fieldValidation = (fieldName: string, val: string) => {
  switch (fieldName) {
    case addPodsFormFields.name: {
      return {
        required: !val && 'Please enter a name',
        max: val && val.length > 64 && 'This name is too long',
      }
    }
    case addPodsFormFields.managers: {
      return {
        required: !val && 'Please select a team member',
      }
    }
    default: {
      return {}
    }
  }
}

const formValidation = formValidationBuilder(fieldValidation)

const FormBody = ({
  formBags,
  toggleModal,
  teamID,
  handleApplyFilter,
  isEditmode,
  podTeamMembers,
}: any) => {
  const { isValid } = formBags

  const modalBody = (
    <StyledFormBody>
      <Text mb={5}>
        Enter a pod name and select one or more team members to include. Pods
        allow members to easily access assigned properties, viewings,
        applications, and activity.
      </Text>

      <StyledFieldWrapper
        id={addPodsFormFields.name}
        name={addPodsFormFields.name}
        label="Pod name"
        required
      >
        <Box maxWidth={'256px'}>
          <InputField
            id={addPodsFormFields.name}
            name={addPodsFormFields.name}
            inputProps={{ placeholder: 'Enter a name' }}
          />
        </Box>
      </StyledFieldWrapper>

      <StyledFieldWrapper
        id={addPodsFormFields.managers}
        name={addPodsFormFields.managers}
        label="Team members"
        required
      >
        <SelectTeamMembersDropdown
          teamID={teamID}
          formBags={formBags}
          dropDownWidth={theme.width[10]}
          handleApplyFilter={handleApplyFilter}
          podTeamMembers={podTeamMembers}
        />
      </StyledFieldWrapper>

      <Flex alignItems="center" mt={theme.space[9]}>
        <Button
          type="button"
          variant="solidSecondary"
          flex="1 1 30%"
          mr={theme.space[3] + 'px'}
          onClick={toggleModal}
        >
          Cancel
        </Button>
        <Button disabled={!isValid} type="submit" flex="1 1 70%">
          Save
        </Button>
      </Flex>
    </StyledFormBody>
  )

  return (
    <Form style={{ height: '100%', width: '100%' }}>
      <Modal
        modalHeading={isEditmode ? 'Edit Pod' : 'Add Pod'}
        modalBody={modalBody}
        toggleModal={toggleModal}
        secondaryAction={undefined}
        modalFooter={undefined}
        primaryLabel={undefined}
        primaryAction={undefined}
        secondaryLabel={undefined}
        customModalFooter
      />
    </Form>
  )
}

export const AddPodModal = ({
  teamID,
  editingPod,
  isEditmode,
  onDismissClick,
  onSubmitForm,
}: AddPodModalProps) => {
  const [podTeamMembers, setPodTeamMembers] = useState<string[]>([])
  const [initialPodValues, setInitialPodValues] =
    useState<typeof initialFormValues>()

  useEffect(() => {
    if (editingPod) {
      setInitialPodValues({
        name: editingPod?.name,
        managers:
          editingPod?.managers?.reduce(
            (acc: { [key: string]: boolean }, item) => {
              acc[item.guidID] = true
              return acc
            },
            {},
          ) || [],
      })
      setPodTeamMembers(
        editingPod?.managers?.map((manager) => manager.guidID) || [],
      )
    } else {
      setInitialPodValues(initialFormValues)
    }
  }, [editingPod])

  const handleApplyFilter = (data: { [key: string]: boolean }) => {
    setPodTeamMembers(Object.keys(data))
  }

  const handleSubmitForm = (
    data: { name: string },
    helpers: FormikHelpers<typeof initialFormValues>,
  ) => {
    if (podTeamMembers.length > 0) {
      onSubmitForm({ name: data?.name, managers: podTeamMembers })
    } else {
      helpers.setErrors({
        [addPodsFormFields.managers]: {
          required: 'Please select a team member',
        },
      })
    }
  }

  if (!initialPodValues) {
    return null
  }

  return (
    <Formik
      initialValues={initialPodValues}
      onSubmit={handleSubmitForm}
      validate={formValidation}
    >
      {(props) => (
        <FormBody
          teamID={teamID}
          formBags={props}
          isEditmode={isEditmode}
          podTeamMembers={podTeamMembers}
          handleApplyFilter={handleApplyFilter}
          toggleModal={onDismissClick}
        />
      )}
    </Formik>
  )
}
